import {
  breadCrumbList,
  eventLocation,
  eventSchemaAttendanceMode,
  eventSchemaStatus,
  listingAddress,
  listingStatusGroup,
  propertyPrice,
  listingPhotos,
  EMPTY_VALUE,
  ADC_DEFAULT_VALUE,
  ADC_URL,
  ADC_POSTAL_ADDRESS_SCHEMA,
  listingPageUrl,
  getValidKeywords,
  DEFAULT_ADC_KEYWORDS,
  DEFAULT_ADC_QA,
  type QAItem,
} from './schemaHelpers'
import type { Listing } from '@adc/sdk/types'
import * as graph from '@adc/sdk'
import { get, isNil, isArray, union } from 'lodash'

// Schema.org type definitions
interface SchemaBase {
  '@context': string
  '@type': string
}

interface BreadcrumbListSchema extends SchemaBase {
  '@type': 'BreadcrumbList'
  itemListElement: Array<{
    '@type': 'ListItem'
    position: number
    item: {
      '@id': string
      name: string
    }
  }>
}

interface EventSchema extends SchemaBase {
  '@type': 'Event'
  name: string
  description: string
  image: string[]
  startDate: string
  endDate?: string
  eventStatus: string
  eventAttendanceMode: string
  keywords: string[]
  location: any[]
  organizer: {
    '@type': string
    '@id': string
    name: string
    url: string
  }
  performer: {
    '@type': string
    name: string
  }
}

interface ProductSchema extends SchemaBase {
  '@type': 'Product'
  name: string
  image: string[]
  description: string
  brand: {
    '@type': string
    name: string
  }
  offers: {
    '@type': string
    url: string
    priceCurrency: string
    price: number
    availability: string
  }
}

interface SingleFamilyResidenceSchema extends SchemaBase {
  '@type': 'SingleFamilyResidence'
  name: string
  description: string
  numberOfRooms: number
  floorSize: {
    '@type': string
    value: number
    unitCode: string
  }
  numberOfBathroomsTotal: number
  numberOfBedrooms: number
  permittedUsage: string
  yearBuilt: number
  address: {
    '@type': string
    addressCountry: string
    addressLocality: string
    addressRegion: string
    postalCode: string
    streetAddress: string
  }
  url: string
  geo?: {
    '@type': string
    latitude: number
    longitude: number
  }
}

interface CorporationSchema {
  '@type': 'Corporation'
  name: string
  url: string
  address: any
  location: any
  logo: {
    '@type': string
    inLanguage: string
    '@id': string
    contentUrl: string
    url: string
    caption: string
  }
  sameAs: string[]
}

interface WebSiteSchema {
  '@type': 'WebSite'
  '@id': string
  url: string
  name: string
  description: string
  publisher: {
    '@id': string
  }
  alternateName: string
  potentialAction: Array<{
    '@type': string
    target: {
      '@type': string
      urlTemplate: string
    }
    'query-input': string
  }>
  inLanguage: string
  sameAs: string[]
  keywords: string[]
}

interface OrganizationSchema {
  '@type': 'Organization'
  '@id': string
  name: string
  description: string
  url: string
  alternateName: string
  logo: {
    '@type': string
    inLanguage: string
    '@id': string
    contentUrl: string
    url: string
    caption: string
  }
  image: {
    '@id': string
  }
  sameAs: string[]
  contactPoint: {
    '@type': string
    telephone: string
    contactType: string
  }
}

interface WebPageSchema {
  '@type': 'WebPage'
  '@id': string
  url: string
  name: string
  isPartOf: {
    '@id': string
  }
  description: string
  breadcrumb: BreadcrumbListSchema
  inLanguage: string
  potentialAction: Array<{
    '@type': string
    target: string[]
  }>
  keywords: string[]
}

interface FAQPageSchema {
  '@type': 'FAQPage'
  '@id': string
  url: string
  name: string
  isPartOf: {
    '@id': string
  }
  description: string
  breadcrumb: BreadcrumbListSchema
  inLanguage: string
  mainEntity: Array<{
    '@type': 'Question'
    name: string
    acceptedAnswer: {
      '@type': 'Answer'
      text: string
    }
  }>
  keywords: string[]
}

/**
 * @description standardSchemas includes JSON object for linking data. For search engine optimization (SEO), Linked data allows Google Search to understand the content of the page.
 * Each schema expects similar fields relative to the listingcard schema.
 */

/**
 * @name breadCrumbSchema
 * @description Below are the graphQL fields associated to the breadCrumbSchema properties
 * @param {*} listing
 * @field {string} primary_property.summary.address.country_primary_subdivision
 * @field {string} primary_property.summary.address.country_secondary_subdivision
 * @field {string} primary_property.summary.street_description
 * @field {string} listing_page_path
 */
const breadCrumbSchema = (listing: Listing): BreadcrumbListSchema => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadCrumbList(listing).map(({ label, url }, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      item: {
        '@id': `${ADC_URL}${url}`,
        name: label,
      },
    })),
  }
}
/**
 * @name eventSchema
 * @param {*} listing
 * @description Below are the graphQL fields associated to the eventSchema properties
 * @field {string} primary_property.summary.formatted_address
 * @field {array.<string>} photos.file_descriptor.urls.cdn
 * @field {string} listing_status_group
 * @field {string} listing_summary.is_remote_bid_enabled
 * @field {string} listing_remarks.mls_comment
 * @field {string} venue.event.event_type
 * @field {string} venue.venue_url
 * @field {string} venue.venue_name
 * @field {string} venue.venue_title
 * @field {string} venue.event.description
 * @field {string} venue.venue_schedule.bid_start_local_date
 * @field {string} venue.venue_address.street_unit
 * @field {string} venue.venue_address.street_number
 * @field {string} venue.venue_address.street_name
 * @field {string} venue.venue_address.municipality
 * @field {string} venue.venue_address.country_primary_subdivision
 * @field {string} venue.venue_address.postal_code
 * @field {string} venue.venue_address.country_code
 */
const eventSchema = (
  listing: Listing,
  defaultImage: string = '',
  keywords: string[] = []
): EventSchema => {
  const { streetAddress, formattedAddress, state, county, city } =
    listingAddress(listing)
  const bidStartDate = get(listing, 'venue.venue_schedule.bid_start_local_date')
  const eventStartDate =
    listingStatusGroup(listing) === graph.enums.ListingStatusGroup.Inactive
      ? new Date().toISOString().substring(0, 10)
      : bidStartDate
      ? String(bidStartDate)
      : new Date().toISOString().substring(0, 10)
  const bidEndDate = get(
    listing,
    'venue.venue_schedule.initial_bidding_end_local_date'
  )
  const eventEndDate =
    listingStatusGroup(listing) === graph.enums.ListingStatusGroup.Inactive
      ? null
      : bidEndDate
      ? String(bidEndDate)
      : null
  const descriptionTitle =
    get(listing, 'venue.venue_title') || ADC_DEFAULT_VALUE
  const descriptionComment = get(listing, 'listing_remarks.mls_comment') || ''
  return {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: `Auction of ${streetAddress} - ${descriptionTitle}`,
    description: `Auction of ${formattedAddress}. ${descriptionComment}`.trim(),
    image: listingPhotos(listing, defaultImage),
    startDate: eventStartDate,
    ...(!isNil(eventEndDate) && {
      endDate: eventEndDate,
    }),
    eventStatus: eventSchemaStatus(listing),
    eventAttendanceMode: eventSchemaAttendanceMode(listing),
    keywords: union(DEFAULT_ADC_KEYWORDS, getValidKeywords(keywords), [
      state,
      county,
      city,
      streetAddress,
      // formattedAddress can be either string or string[] from SDK Listing type
      // If array: spread to flatten it (removing nulls), if string: wrap in array then spread
      ...(isArray(formattedAddress)
        ? formattedAddress.filter(f => f != null)
        : [formattedAddress]),
      get(listing, 'venue.venue_title') || ADC_DEFAULT_VALUE,
    ]),
    location: eventLocation(listing),
    organizer: {
      '@type': 'Organization',
      '@id': `${ADC_URL}/#organization`,
      name: ADC_DEFAULT_VALUE,
      url: ADC_URL,
    },
    performer: {
      '@type': 'PerformingGroup',
      name: get(listing, 'venue.venue_title') || ADC_DEFAULT_VALUE,
    },
  }
}
/**
 * @name productSchema
 * @param {*} listing
 * @description Below are the graphQL fields associated to the productSchema properties
 * @field {string} primary_property.summary.formatted_address
 * @field {string} primary_property.summary.valuation'
 * @field {string} primary_photo
 * @field {string} property_details.property_description
 * @field {string} listing_page_path
 * @field {string} auction.starting_bid
 * @field {string} listing_status_group
 */
const productSchema = (
  listing: Listing,
  defaultImage: string
): ProductSchema => {
  const { formattedAddress } = listingAddress(listing)
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: isArray(formattedAddress)
      ? formattedAddress.join(' ')
      : formattedAddress,
    image: listingPhotos(listing, defaultImage),
    description:
      get(listing, 'listing_remarks.legal_description') || EMPTY_VALUE,
    brand: {
      '@type': 'Brand',
      name: ADC_DEFAULT_VALUE,
    },
    offers: {
      '@type': 'Offer',
      url: listingPageUrl(listing),
      priceCurrency: 'USD',
      price: propertyPrice(listing),
      availability:
        listingStatusGroup(listing) === graph.enums.ListingStatusGroup.Sold
          ? 'OutOfStock'
          : 'InStock',
    },
  }
}
/**
 * @name singleFamilyHomeSchema
 * @param {*} listing
 * @description Below are the graphQL fields associated to the singleFamilyHomeSchema properties
 * @field {string} primary_property.summary.formatted_address
 * @field {string} primary_property.summary.address.country_primary_subdivision
 * @field {string} primary_property.summary.address.country_secondary_subdivision
 * @field {string} primary_property.summary.address.coordinates.lat
 * @field {string} primary_property.summary.address.coordinates.lon
 * @field {string} listing_page_path
 * @field {string} primary_property.summary.street_address
 * @field {string} primary_property.summary.postal_code
 * @field {string} primary_property.summary.country_code
 * @field {string} primary_property.summary.year_built
 * @field {integer} primary_property.bedrooms
 * @field {integer} primary_property.summary.living_size
 * @field {float} primary_property.bathrooms
 * @field {string} listing_remarks.legal_description
 * @field {string} listing_remarks.mls_comment
 */
const singleFamilyHomeSchema = (
  listing: Listing
): SingleFamilyResidenceSchema => {
  const {
    state,
    county,
    streetAddress,
    postalCode,
    countryCode,
    formattedAddress,
  } = listingAddress(listing)
  const lat = get(listing, 'primary_property.summary.address.coordinates.lat')
  const lon = get(listing, 'primary_property.summary.address.coordinates.lon')

  return {
    '@context': 'https://schema.org',
    '@type': 'SingleFamilyResidence',
    name: isArray(formattedAddress)
      ? formattedAddress.join(' ')
      : formattedAddress,
    description: get(listing, 'listing_remarks.mls_comment') || EMPTY_VALUE,
    numberOfRooms: get(listing, 'primary_property.summary.total_bedrooms') || 0,
    floorSize: {
      '@type': 'QuantitativeValue',
      value:
        get(listing, 'primary_property.summary.square_footage') ||
        get(listing, 'primary_property.summary.living_size') ||
        0,
      unitCode: 'FTK',
    },
    numberOfBathroomsTotal:
      get(listing, 'primary_property.summary.total_bathrooms') || 0,
    numberOfBedrooms:
      get(listing, 'primary_property.summary.total_bedrooms') || 0,
    permittedUsage:
      get(listing, 'listing_remarks.legal_description') || EMPTY_VALUE,
    yearBuilt:
      get(listing, 'primary_property.summary.year_built') ||
      new Date().getFullYear(),
    address: {
      '@type': 'PostalAddress',
      addressCountry: countryCode,
      addressLocality: county,
      addressRegion: state,
      postalCode,
      streetAddress,
    },
    url: listingPageUrl(listing) || 'https://www.auction.com',
    ...(lat && lon
      ? {
          geo: {
            '@type': 'GeoCoordinates',
            latitude: lat,
            longitude: lon,
          },
        }
      : {}),
  }
}

const corporateSchema = (logoImage: string): CorporationSchema => ({
  '@type': 'Corporation',
  name: ADC_DEFAULT_VALUE,
  url: ADC_URL,
  address: ADC_POSTAL_ADDRESS_SCHEMA,
  location: ADC_POSTAL_ADDRESS_SCHEMA,
  logo: {
    '@type': 'ImageObject',
    inLanguage: 'en-US',
    '@id': `${ADC_URL}/#logo`,
    contentUrl: logoImage,
    url: logoImage,
    caption: ADC_DEFAULT_VALUE,
  },
  sameAs: [
    'https://www.facebook.com/Auctioncom',
    'https://www.youtube.com/@auctionsnow',
    'https://www.linkedin.com/company/auction',
    'https://twitter.com/auction',
    'https://www.instagram.com/Auction_Com/',
    'https://www.auction.com/lp/about-us/',
    'https://www.auction.com/lp/blog/',
  ],
})

const searchSiteSchema = (keywords: string[] = []): WebSiteSchema => {
  return {
    '@type': 'WebSite',
    '@id': `${ADC_URL}/#website`,
    url: ADC_URL,
    name: ADC_DEFAULT_VALUE,
    description:
      'Auction.com: Real Estate Auctions for Foreclosures and Bank Owned Properties',
    publisher: {
      '@id': `${ADC_URL}/#organization`,
    },
    alternateName: ADC_DEFAULT_VALUE,
    potentialAction: [
      {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${ADC_URL}/residential/{search_term_string}_qs`,
        },
        'query-input': 'required name=search_term_string',
      },
    ],
    inLanguage: 'en-US',
    sameAs: [
      'https://www.facebook.com/Auctioncom',
      'https://www.youtube.com/@auctionsnow',
      'https://www.linkedin.com/company/auction',
      'https://twitter.com/auction',
      'https://www.instagram.com/Auction_Com/',
      'https://www.auction.com/lp/about-us/',
      'https://www.auction.com/lp/blog/',
    ],
    keywords: union(DEFAULT_ADC_KEYWORDS, getValidKeywords(keywords)),
  }
}

const organizationSchema = (logoImage: string): OrganizationSchema => {
  return {
    '@type': 'Organization',
    '@id': `${ADC_URL}/#organization`,
    name: ADC_DEFAULT_VALUE,
    description:
      'Auction.com: Real Estate Auctions for Foreclosures and Bank Owned Properties',
    url: ADC_URL,
    alternateName: ADC_DEFAULT_VALUE,
    logo: {
      '@type': 'ImageObject',
      inLanguage: 'en-US',
      '@id': `${ADC_URL}/#logo`,
      contentUrl: logoImage,
      url: logoImage,
      caption: ADC_DEFAULT_VALUE,
    },
    image: {
      '@id': `${ADC_URL}/#logo`,
    },
    sameAs: [
      'https://www.facebook.com/Auctioncom',
      'https://www.youtube.com/@auctionsnow',
      'https://www.linkedin.com/company/auction',
      'https://twitter.com/auction',
      'https://www.instagram.com/Auction_Com/',
      'https://www.auction.com/lp/about-us/',
      'https://www.auction.com/lp/blog/',
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '(800) 793-6107',
      contactType: 'Customer Support',
    },
  }
}
const defaultBreadCrumb: any = {
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      item: {
        '@id': `${ADC_URL}`,
        name: `${ADC_DEFAULT_VALUE}`,
      },
    },
  ],
}
const webPageSchema = (
  url: string = ADC_URL,
  name: string = ADC_DEFAULT_VALUE,
  description: string = 'Auction.com: Real Estate Auctions for Foreclosures and Bank Owned Properties',
  breadcrumb: BreadcrumbListSchema = defaultBreadCrumb,
  keywords: string[] = []
): WebPageSchema => {
  return {
    '@type': 'WebPage',
    '@id': url,
    url,
    name,
    isPartOf: {
      '@id': `${ADC_URL}/#website`,
    },
    description,
    breadcrumb,
    inLanguage: 'en-US',
    potentialAction: [
      {
        '@type': 'ReadAction',
        target: [url],
      },
    ],
    keywords: union(DEFAULT_ADC_KEYWORDS, getValidKeywords(keywords)),
  }
}

const faqPageSchema = (
  url: string = ADC_URL,
  name: string = ADC_DEFAULT_VALUE,
  description: string = 'Auction.com: Real Estate Auctions for Foreclosures and Bank Owned Properties',
  breadcrumb: BreadcrumbListSchema = defaultBreadCrumb,
  qaList: QAItem[] = DEFAULT_ADC_QA,
  keywords: string[] = []
): FAQPageSchema => {
  const qaSchema = qaList.map(qa => {
    return {
      '@type': 'Question' as const,
      name: qa.question,
      acceptedAnswer: {
        '@type': 'Answer' as const,
        text: qa.answer,
      },
    }
  })

  return {
    '@type': 'FAQPage',
    '@id': url,
    url,
    name,
    isPartOf: {
      '@id': `${ADC_URL}/#website`,
    },
    description,
    breadcrumb,
    inLanguage: 'en-US',
    mainEntity: qaSchema,
    keywords: union(DEFAULT_ADC_KEYWORDS, getValidKeywords(keywords)),
  }
}

export {
  breadCrumbSchema,
  eventSchema,
  productSchema,
  singleFamilyHomeSchema,
  corporateSchema,
  searchSiteSchema,
  organizationSchema,
  webPageSchema,
  faqPageSchema,
}
